"use client";

import { useQuery } from "@tanstack/react-query";
import { apiPost } from "@/lib/api";

interface CreateShortUrlResponse {
  trail: string;
}

/**
 * A short link for `pageUrl`, minted by the backend once `enabled`.
 *
 * The share dialog opens on the full, state-heavy page URL; this returns a short
 * `…/s/<trail>` link to swap in when the backend has one. The link is built from
 * `window.location.origin`, so it lives on the tenant's own domain (served by the
 * frontend's `/s/<trail>` route) and redirects back to the same origin.
 *
 * Keyed on the URL and never stale — a short link for a fixed URL doesn't change
 * — so reopening the same view reuses the trail with no round trip. The create
 * endpoint is idempotent anyway, so even a cache miss won't mint a duplicate.
 */
export function useShortUrl(pageUrl: string, enabled: boolean) {
  const query = useQuery({
    queryKey: ["short-url", pageUrl],
    queryFn: ({ signal }) =>
      apiPost<CreateShortUrlResponse>("/short-urls", { url: pageUrl }, { signal }),
    enabled: enabled && !!pageUrl,
    staleTime: Infinity,
    retry: false,
  });

  const shortUrl =
    query.data && typeof window !== "undefined"
      ? `${window.location.origin}/s/${query.data.trail}`
      : null;

  return { shortUrl, isPending: query.isLoading, isError: query.isError };
}
